// src/components/LoginSupportWidget.js
import React, { useState, useCallback, useEffect, useRef } from 'react';
import api from '../services/api';
import { FaHeadset, FaTimes, FaBuilding, FaArrowRight, FaCheckCircle, FaExclamationTriangle, FaSms, FaRedoAlt } from 'react-icons/fa';

// ─────────────────────────────────────────────────────────────────────────────
// ESTILOS INLINE
// ─────────────────────────────────────────────────────────────────────────────
const S = {
  triggerBtn: {
    position: 'fixed', bottom: 32, right: 32,
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '13px 22px',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    color: '#fff', border: 'none', borderRadius: 50,
    fontFamily: "'Inter', sans-serif", fontSize: '0.9rem', fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 8px 32px rgba(15,23,42,0.35), 0 2px 8px rgba(0,229,191,0.15)',
    zIndex: 999999,
    transition: 'transform 0.25s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.25s ease',
    letterSpacing: '0.01em',
  },
  triggerDot: {
    width: 8, height: 8, borderRadius: '50%', background: '#00e5bf',
    display: 'inline-block', marginRight: -4,
    animation: 'supportPulse 2s ease-in-out infinite',
  },
  modal: {
    position: 'fixed', bottom: 90, right: 32, width: 360,
    background: '#ffffff', borderRadius: 20,
    boxShadow: '0 24px 64px rgba(15,23,42,0.22), 0 0 0 1px rgba(0,0,0,0.04)',
    zIndex: 999999, fontFamily: "'Inter', sans-serif", overflow: 'hidden',
    animation: 'supportSlideUp 0.3s cubic-bezier(0.16,1,0.3,1) forwards',
  },
  header: {
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    padding: '18px 20px', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', gap: 12,
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  headerIcon: {
    width: 34, height: 34, borderRadius: 10,
    background: 'rgba(0,229,191,0.15)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', color: '#00e5bf', flexShrink: 0,
  },
  headerTitle:    { color: '#fff', fontWeight: 700, fontSize: '1rem', margin: 0, lineHeight: 1.2 },
  headerSubtitle: { color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', margin: 0, lineHeight: 1, marginTop: 2 },
  closeBtn: {
    background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8,
    width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', color: 'rgba(255,255,255,0.7)',
    transition: 'background 0.2s, color 0.2s', flexShrink: 0,
  },
  tenantBadge: {
    margin: '16px 20px 0', padding: '10px 14px', background: '#f0fdf4',
    border: '1px solid #bbf7d0', borderRadius: 10, display: 'flex',
    alignItems: 'center', gap: 8, color: '#15803d', fontSize: '0.82rem', fontWeight: 600,
  },
  tenantBadgeWarn: { background: '#fefce8', border: '1px solid #fde68a', color: '#92400e' },
  body:      { padding: '20px 20px 24px' },
  bodyTitle: { color: '#334155', fontSize: '0.88rem', fontWeight: 500, margin: '0 0 16px', lineHeight: 1.5 },
  inputGroup: { marginBottom: 12 },
  input: {
    width: '100%', padding: '12px 14px', border: '1.5px solid #e2e8f0', borderRadius: 10,
    fontFamily: "'Inter', sans-serif", fontSize: '0.9rem', color: '#1e293b',
    background: '#f8fafc', boxSizing: 'border-box', outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s, background 0.2s',
  },
  inputToken: {
    width: '100%', padding: '16px 14px', border: '2px solid #00e5bf', borderRadius: 12,
    fontFamily: "'Inter', sans-serif", fontSize: '1.6rem', fontWeight: 800,
    color: '#0f172a', background: '#f0fffe', boxSizing: 'border-box', outline: 'none',
    textAlign: 'center', letterSpacing: '0.4em',
    boxShadow: '0 0 0 4px rgba(0,229,191,0.12)',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  },
  textarea: {
    width: '100%', padding: '12px 14px', border: '1.5px solid #e2e8f0', borderRadius: 10,
    fontFamily: "'Inter', sans-serif", fontSize: '0.9rem', color: '#1e293b',
    background: '#f8fafc', boxSizing: 'border-box', outline: 'none', resize: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s, background 0.2s', minHeight: 80,
  },
  smsHint: {
    display: 'flex', alignItems: 'flex-start', gap: 8, color: '#0f766e',
    fontSize: '0.82rem', marginBottom: 16, padding: '10px 12px',
    background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0', lineHeight: 1.4,
  },

  // ── Reenvio SMS ──────────────────────────────────────────────────────────
  resendBox: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 14px', background: '#f8fafc', borderRadius: 8,
    border: '1px solid #e2e8f0', marginBottom: 14, gap: 8,
  },
  resendTimer: {
    fontSize: '0.8rem', color: '#64748b', fontWeight: 500,
  },
  resendBtn: {
    display: 'flex', alignItems: 'center', gap: 5,
    background: 'none', border: '1px solid #00e5bf', borderRadius: 8,
    color: '#00a88d', fontFamily: "'Inter', sans-serif", fontSize: '0.8rem',
    fontWeight: 700, padding: '6px 12px', cursor: 'pointer',
    transition: 'background 0.2s, color 0.2s',
    flexShrink: 0,
  },
  resendBtnDisabled: {
    display: 'flex', alignItems: 'center', gap: 5,
    background: 'none', border: '1px solid #e2e8f0', borderRadius: 8,
    color: '#94a3b8', fontFamily: "'Inter', sans-serif", fontSize: '0.8rem',
    fontWeight: 700, padding: '6px 12px', cursor: 'not-allowed',
    flexShrink: 0,
  },

  submitBtn: {
    width: '100%', padding: '14px',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    color: '#fff', border: 'none', borderRadius: 10,
    fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: '0.95rem',
    cursor: 'pointer', display: 'flex', alignItems: 'center',
    justifyContent: 'center', gap: 8,
    transition: 'transform 0.2s, box-shadow 0.2s',
    boxShadow: '0 4px 12px rgba(15,23,42,0.2)', marginTop: 4,
  },
  successBox:   { padding: '28px 20px 32px', textAlign: 'center' },
  successIcon:  { color: '#00e5bf', fontSize: '2.8rem', marginBottom: 14 },
  successTitle: { color: '#0f172a', fontWeight: 800, fontSize: '1.15rem', margin: '0 0 10px' },
  successText:  { color: '#64748b', fontSize: '0.88rem', lineHeight: 1.6, margin: '0 0 24px' },
  errorBox: {
    background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
    padding: '10px 14px', color: '#dc2626', fontSize: '0.85rem',
    fontWeight: 500, marginBottom: 12,
  },
  steps: { display: 'flex', gap: 4, padding: '12px 20px 0' },
  step: (active) => ({
    height: 3, flex: 1, borderRadius: 3,
    background: active ? '#00e5bf' : '#e2e8f0', transition: 'background 0.3s',
  }),
  divider: { height: 1, background: '#f1f5f9', margin: '16px 0' },
};

// Keyframes injetados uma única vez
if (typeof document !== 'undefined' && !document.getElementById('support-widget-styles')) {
  const style = document.createElement('style');
  style.id = 'support-widget-styles';
  style.textContent = `
    @keyframes supportPulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50%       { opacity: 0.5; transform: scale(0.7); }
    }
    @keyframes supportSlideUp {
      from { opacity: 0; transform: translateY(16px) scale(0.97); }
      to   { opacity: 1; transform: translateY(0)    scale(1);    }
    }
    #support-trigger-btn:hover {
      transform: translateY(-3px) scale(1.02) !important;
      box-shadow: 0 12px 40px rgba(15,23,42,0.4), 0 4px 12px rgba(0,229,191,0.2) !important;
    }
    .support-input:focus {
      border-color: #00e5bf !important;
      box-shadow: 0 0 0 3px rgba(0,229,191,0.15) !important;
      background: #fff !important;
    }
    .support-submit-btn:hover:not(:disabled) {
      transform: translateY(-1px) !important;
      box-shadow: 0 8px 24px rgba(15,23,42,0.3) !important;
    }
    .support-submit-btn:disabled { opacity: 0.6; cursor: not-allowed !important; }
    .support-close-btn:hover { background: rgba(255,255,255,0.2) !important; color: #fff !important; }
    .resend-btn-active:hover { background: rgba(0,229,191,0.08) !important; }
  `;
  document.head.appendChild(style);
}

// ── Esperas de reenvio: 1min → 5min → 10min → 10min (máx) ─────────────────
const RESEND_WAITS = [60, 300, 600, 600]; // segundos

function formatCountdown(secs) {
  if (secs >= 60) {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
  }
  return `${secs}s`;
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────
export default function LoginSupportWidget({ empresaId, empresaNome }) {
  const [open, setOpen]       = useState(false);
  const [stage, setStage]     = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const [identity, setIdentity] = useState({ email: '', cpf: '', matricula: '' });
  const [userId, setUserId]     = useState(null);
  const [ticket, setTicket]     = useState({ token: '', assunto: '', descricao: '' });

  // ── Controle de reenvio ──────────────────────────────────────────────────
  const [resendCount,    setResendCount]    = useState(0);   // quantas vezes reenviou
  const [resendCooldown, setResendCooldown] = useState(0);   // segundos restantes
  const [resendLoading,  setResendLoading]  = useState(false);
  const timerRef = useRef(null);

  // Inicia countdown quando entra no stage 2
  const startCooldown = useCallback((attemptIndex) => {
    const wait = RESEND_WAITS[Math.min(attemptIndex, RESEND_WAITS.length - 1)];
    setResendCooldown(wait);

    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // Limpa timer ao desmontar ou fechar
  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  // ── Reset completo ───────────────────────────────────────────────────────
  const resetWidget = useCallback(() => {
    setStage(1); setError(''); setUserId(null);
    setIdentity({ email: '', cpf: '', matricula: '' });
    setTicket({ token: '', assunto: '', descricao: '' });
    setLoading(false); setResendCount(0); setResendCooldown(0); setResendLoading(false);
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setTimeout(resetWidget, 300);
  }, [resetWidget]);

  const handleIdentityChange = useCallback((e) => {
    const { name, value } = e.target;
    setIdentity(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleTicketChange = useCallback((e) => {
    const { name, value } = e.target;
    setTicket(prev => ({ ...prev, [name]: value }));
  }, []);

  // ── Stage 1 → 2 ─────────────────────────────────────────────────────────
  const handleValidateIdentity = useCallback(async () => {
    if (!identity.email || !identity.cpf) {
      setError('Preencha ao menos e-mail e CPF.'); return;
    }
    setError(''); setLoading(true);
    try {
      const res = await api.post('/api/public/support/identify', {
        email:      identity.email.trim().toLowerCase(),
        cpf:        identity.cpf,
        matricula:  identity.matricula || undefined,
        empresa_id: empresaId || null,
      });
      setUserId(res.data.userId);
      setResendCount(0);
      setStage(2);
      startCooldown(0); // primeira espera: 1 min
    } catch (err) {
      setError(err?.response?.data?.error || 'Não foi possível validar sua identidade.');
    } finally {
      setLoading(false);
    }
  }, [identity, empresaId, startCooldown]);

  // ── Reenvio de SMS ───────────────────────────────────────────────────────
  const handleResend = useCallback(async () => {
    if (resendCooldown > 0 || resendLoading) return;
    setResendLoading(true); setError('');
    try {
      await api.post('/api/public/support/resend', { userId });
      const nextAttempt = resendCount + 1;
      setResendCount(nextAttempt);
      setTicket(prev => ({ ...prev, token: '' })); // limpa token antigo
      startCooldown(nextAttempt);
    } catch (err) {
      setError(err?.response?.data?.error || 'Falha ao reenviar. Tente novamente.');
    } finally {
      setResendLoading(false);
    }
  }, [resendCooldown, resendLoading, userId, resendCount, startCooldown]);

  // ── Stage 2 → 3 ─────────────────────────────────────────────────────────
  const handleSubmitTicket = useCallback(async () => {
    if (!ticket.token || ticket.token.replace(/\D/g, '').length < 6) {
      setError('Digite o código de 6 dígitos recebido por SMS.'); return;
    }
    if (!ticket.assunto.trim()) {
      setError('Informe o assunto do chamado.'); return;
    }
    setError(''); setLoading(true);
    try {
      await api.post('/api/public/support/open-ticket', {
        userId,
        token: ticket.token.replace(/\D/g, ''),
        msg:   `${ticket.assunto.trim()}\n\n${ticket.descricao.trim() || ''}`.trim(),
      });
      setStage(3);
    } catch (err) {
      setError(err?.response?.data?.error || 'Erro ao abrir chamado. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [userId, ticket]);

  const tenantOk = Boolean(empresaId && empresaNome);

  // ── Texto do botão de reenvio ────────────────────────────────────────────
  const resendLabel = resendCount === 0
    ? 'Não recebeu?'
    : `Reenviar novamente (${resendCount}ª vez)`;

  return (
    <>
      {/* ── Botão flutuante ── */}
      <button
        id="support-trigger-btn"
        onClick={() => setOpen(o => !o)}
        style={S.triggerBtn}
        title="Precisa de ajuda?"
        aria-label="Abrir suporte ao acesso"
      >
        <span style={S.triggerDot} />
        <FaHeadset size={15} />
        <span>Precisa de ajuda?</span>
      </button>

      {open && (
        <div style={S.modal} role="dialog" aria-modal="true" aria-label="Suporte ao Acesso">

          {/* Header */}
          <div style={S.header}>
            <div style={S.headerLeft}>
              <div style={S.headerIcon}><FaHeadset size={15} /></div>
              <div>
                <p style={S.headerTitle}>Suporte ao Acesso</p>
                <p style={S.headerSubtitle}>Atendimento {tenantOk ? empresaNome : 'Geral'}</p>
              </div>
            </div>
            <button className="support-close-btn" onClick={handleClose} style={S.closeBtn} aria-label="Fechar">
              <FaTimes size={12} />
            </button>
          </div>

          {/* Steps */}
          {stage < 3 && (
            <div style={S.steps}>
              <div style={S.step(stage >= 1)} />
              <div style={S.step(stage >= 2)} />
            </div>
          )}

          {/* Tenant badge */}
          {stage < 3 && (
            <div style={{ ...S.tenantBadge, ...(tenantOk ? {} : S.tenantBadgeWarn) }}>
              {tenantOk
                ? <><FaBuilding size={12} /> Chamado será aberto para: <strong>{empresaNome}</strong></>
                : <><FaExclamationTriangle size={12} /> Selecione sua empresa para direcionar o chamado</>
              }
            </div>
          )}

          {/* ── STAGE 1 ── */}
          {stage === 1 && (
            <div style={S.body}>
              <p style={S.bodyTitle}>Confirme sua identidade para abrir um chamado:</p>
              {error && <div style={S.errorBox}>{error}</div>}

              {['email', 'cpf', 'matricula'].map((field) => (
                <div key={field} style={S.inputGroup}>
                  <input
                    className="support-input"
                    style={S.input}
                    type={field === 'email' ? 'email' : 'text'}
                    name={field}
                    placeholder={
                      field === 'email'    ? 'Seu E-mail' :
                      field === 'cpf'      ? 'Seu CPF (somente números)' :
                                             'Sua Matrícula'
                    }
                    value={identity[field]}
                    onChange={handleIdentityChange}
                    maxLength={field === 'cpf' ? 14 : undefined}
                    inputMode={field === 'cpf' ? 'numeric' : undefined}
                    autoComplete={field === 'email' ? 'email' : 'off'}
                  />
                </div>
              ))}

              <button className="support-submit-btn" style={S.submitBtn} onClick={handleValidateIdentity} disabled={loading}>
                {loading ? 'Validando…' : <><span>Próximo</span><FaArrowRight size={13} /></>}
              </button>
            </div>
          )}

          {/* ── STAGE 2 ── */}
          {stage === 2 && (
            <div style={S.body}>
              <div style={S.smsHint}>
                <FaSms size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>Um código de 6 dígitos foi enviado para o celular cadastrado. Digite abaixo para continuar.</span>
              </div>

              {error && <div style={S.errorBox}>{error}</div>}

              {/* Token */}
              <div style={S.inputGroup}>
                <input
                  className="support-input"
                  style={S.inputToken}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="● ● ● ● ● ●"
                  value={ticket.token}
                  onChange={e => setTicket(prev => ({ ...prev, token: e.target.value.replace(/\D/g, '') }))}
                  autoFocus
                />
              </div>

              {/* ── Reenvio com countdown ── */}
              <div style={S.resendBox}>
                <span style={S.resendTimer}>
                  {resendCooldown > 0
                    ? `Novo código disponível em ${formatCountdown(resendCooldown)}`
                    : resendLabel
                  }
                </span>
                <button
                  className={resendCooldown === 0 && !resendLoading ? 'resend-btn-active' : ''}
                  style={resendCooldown === 0 && !resendLoading ? S.resendBtn : S.resendBtnDisabled}
                  onClick={handleResend}
                  disabled={resendCooldown > 0 || resendLoading}
                  title={resendCooldown > 0 ? `Aguarde ${formatCountdown(resendCooldown)}` : 'Reenviar código SMS'}
                >
                  <FaRedoAlt size={11} />
                  {resendLoading ? 'Enviando…' : 'Reenviar'}
                </button>
              </div>

              <div style={S.divider} />
              <p style={{ ...S.bodyTitle, marginBottom: 12 }}>Descreva o problema de acesso:</p>

              <div style={S.inputGroup}>
                <input
                  className="support-input"
                  style={S.input}
                  type="text"
                  name="assunto"
                  placeholder="Assunto (ex: não consigo entrar)"
                  value={ticket.assunto}
                  onChange={handleTicketChange}
                />
              </div>
              <div style={S.inputGroup}>
                <textarea
                  className="support-input"
                  style={S.textarea}
                  name="descricao"
                  placeholder="Descreva com mais detalhes (opcional)…"
                  value={ticket.descricao}
                  onChange={handleTicketChange}
                />
              </div>

              <button className="support-submit-btn" style={S.submitBtn} onClick={handleSubmitTicket} disabled={loading}>
                {loading ? 'Enviando…' : <><span>Enviar Chamado</span><FaArrowRight size={13} /></>}
              </button>
            </div>
          )}

          {/* ── STAGE 3 ── */}
          {stage === 3 && (
            <div style={S.successBox}>
              <div style={S.successIcon}><FaCheckCircle /></div>
              <p style={S.successTitle}>Chamado aberto!</p>
              <p style={S.successText}>
                Seu pedido foi registrado{tenantOk ? ` para ${empresaNome}` : ''} e nossa equipe entrará em contato pelo número cadastrado em breve.
              </p>
              <button className="support-submit-btn" style={S.submitBtn} onClick={handleClose}>Fechar</button>
            </div>
          )}

        </div>
      )}
    </>
  );
}